import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API, apiError, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { LongTextarea, NoteView } from "@/components/LongText";
import { STATO_META, PRIO_META } from "@/pages/Tickets";
import { ArrowLeft, Send, Paperclip, Download, User, Clock, Link2, Loader2, PlayCircle, HelpCircle, CheckCircle2, Lock } from "lucide-react";

export default function TicketDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isSeg = user?.role === "segreteria";
  const [t, setT] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get(`/tickets/${id}`).then((r) => setT(r.data)).catch((e) => { toast.error(apiError(e)); nav("/ticket"); });
  useEffect(() => { load(); }, [id]);

  const send = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try { await api.post(`/tickets/${id}/messaggi`, { testo: msg }); setMsg(""); await load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const setStato = async (stato) => {
    setBusy(true);
    try { await api.post(`/tickets/${id}/stato`, { stato }); toast.success("Stato aggiornato"); await load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/tickets/${id}/allegati`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Allegato caricato"); await load(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const download = async (a) => {
    try {
      const res = await api.get(`/tickets/${id}/allegati/${a.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a"); link.href = url; link.download = a.original_filename || "allegato"; link.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!t) return <div className="skeleton h-96 rounded-2xl" />;
  const sm = STATO_META[t.stato] || {};
  const pm = PRIO_META[t.priorita] || {};
  const chiuso = t.stato === "chiuso";

  return (
    <div className="fade-up pb-10" data-testid="ticket-detail">

      <div className="card-immo p-5 mb-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span data-testid="detail-stato" className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase border ${sm.cls}`}>{sm.label}</span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${pm.cls}`}>{pm.label}</span>
              {t.stato === "in_attesa" && t.queue_position && <span className="text-[11px] font-semibold text-[#FFB020]">Posizione in coda: {t.queue_position}</span>}
            </div>
            <h1 className="font-display text-2xl font-extrabold">{t.titolo}</h1>
            <div className="text-xs text-[var(--muted)] flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
              <span className="flex items-center gap-1"><User size={12} />{t.richiedente_nome}</span>
              <span className="flex items-center gap-1"><Clock size={12} />{fmtDate(t.created_at)}</span>
              {t.collegamento?.label && <span className="flex items-center gap-1"><Link2 size={12} />{t.collegamento.tipo}: {t.collegamento.label}</span>}
              {t.preso_in_carico_da_nome && <span>In carico a: <b className="text-slate-600">{t.preso_in_carico_da_nome}</b></span>}
            </div>
          </div>
        </div>
        <NoteView testid="ticket-descrizione-view" text={t.descrizione} lines={6} className="text-sm text-slate-700 mt-3" />

        {isSeg && !chiuso && (
          <div className="flex flex-wrap gap-2 mt-4" data-testid="seg-actions">
            {t.stato === "in_attesa" && <button data-testid="act-prendi" disabled={busy} onClick={() => setStato("in_lavorazione")} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#38BDF8] text-black text-xs font-semibold disabled:opacity-60"><PlayCircle size={14} /> Prendi in carico</button>}
            {(t.stato === "in_lavorazione" || t.stato === "attesa_info") && <button data-testid="act-info" disabled={busy} onClick={() => setStato("attesa_info")} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-violet-500 text-white text-xs font-semibold disabled:opacity-60"><HelpCircle size={14} /> Richiedi informazioni</button>}
            {t.stato !== "completato" && t.stato !== "in_attesa" && <button data-testid="act-completa" disabled={busy} onClick={() => setStato("completato")} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-500 text-white text-xs font-semibold disabled:opacity-60"><CheckCircle2 size={14} /> Completa</button>}
            <button data-testid="act-chiudi" disabled={busy} onClick={() => setStato("chiuso")} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-[var(--border)] text-slate-600 text-xs font-semibold hover:bg-slate-100 disabled:opacity-60"><Lock size={14} /> Chiudi</button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 card-immo p-4">
          <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--muted)] mb-3">Messaggi</div>
          <div className="space-y-3 mb-4" data-testid="ticket-messaggi">
            {(t.messaggi || []).length === 0 && <div className="text-sm text-[var(--muted)]">Nessun messaggio.</div>}
            {(t.messaggi || []).map((m) => (
              <div key={m.id} className={`p-3 rounded-lg ${m.autore_role === "segreteria" ? "bg-violet-500/10 border border-violet-500/20" : "bg-slate-50 border border-[var(--border)]"}`}>
                <div className="text-[11px] text-[var(--muted)] mb-1 flex items-center gap-2"><b className="text-slate-700">{m.autore_nome}</b> · {fmtDate(m.at)}</div>
                <NoteView text={m.testo} lines={6} className="text-sm text-slate-700" />
              </div>
            ))}
          </div>
          {chiuso ? (
            <div className="text-xs text-[var(--muted)] flex items-center gap-1.5"><Lock size={13} /> Ticket chiuso: non è più possibile aggiungere messaggi.</div>
          ) : (
            <div className="flex items-end gap-2">
              <LongTextarea data-testid="msg-input" rows={2} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Scrivi un messaggio o aggiungi informazioni…"
                className="flex-1 px-3 py-2 rounded-lg bg-white border border-[var(--border)] focus:border-[#FC82ED] outline-none text-sm" />
              <button data-testid="msg-send" disabled={busy || !msg.trim()} onClick={send} className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg bg-[#FC82ED] text-black text-sm font-semibold disabled:opacity-50">{busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}</button>
            </div>
          )}
        </div>

        <div className="card-immo p-4">
          <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--muted)] mb-3">Allegati</div>
          <div className="space-y-2 mb-3" data-testid="ticket-allegati">
            {(t.allegati || []).length === 0 && <div className="text-sm text-[var(--muted)]">Nessun allegato.</div>}
            {(t.allegati || []).map((a) => (
              <button key={a.id} data-testid={`allegato-${a.id}`} onClick={() => download(a)} className="w-full flex items-center gap-2 text-left p-2 rounded-lg border border-[var(--border)] hover:border-[#FC82ED]/40 text-xs">
                <Paperclip size={13} className="text-[#FC82ED] shrink-0" />
                <span className="flex-1 truncate">{a.original_filename}</span>
                <Download size={13} className="text-[var(--muted)] shrink-0" />
              </button>
            ))}
          </div>
          {!chiuso && (
            <>
              <input ref={fileRef} type="file" data-testid="allegato-input" onChange={upload} className="hidden" />
              <button data-testid="allegato-btn" disabled={busy} onClick={() => fileRef.current?.click()} className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-dashed border-[#FC82ED]/40 text-[#FC82ED] text-xs font-semibold hover:bg-[#FC82ED]/5 disabled:opacity-60"><Paperclip size={13} /> Aggiungi allegato</button>
            </>
          )}

          <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--muted)] mt-5 mb-2">Storico</div>
          <div className="space-y-1.5" data-testid="ticket-storico">
            {(t.storico || []).map((h, i) => (
              <div key={i} className="text-[11px] text-[var(--muted)] flex items-start gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FC82ED] mt-1 shrink-0" />
                <span>{fmtDate(h.at)} · {h.by_nome} · {h.azione === "cambio_stato" ? `${STATO_META[h.da_stato]?.label || h.da_stato} → ${STATO_META[h.a_stato]?.label || h.a_stato}` : h.azione}{h.dettaglio ? ` (${h.dettaglio})` : ""}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
